'use client'

import { Suspense, useSyncExternalStore } from 'react'
import { LazyMessages } from '@/components/i18n/lazy-messages'
import { DeferredPanelBoundary } from '@/components/layout/deferred-panel-boundary'
import {
  LazyProductDemo,
  ProductDemoPoster,
} from '@/components/product-demo/lazy-product-demo'

/** The demo is built for a desktop canvas; smaller screens see the picture. */
const DESKTOP_QUERY = '(min-width: 1024px)'

function subscribeToDesktop(onChange: () => void) {
  const query = window.matchMedia(DESKTOP_QUERY)
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

const isDesktop = () => window.matchMedia(DESKTOP_QUERY).matches
const isDesktopOnServer = () => false

/**
 * The hero's live product demo (`components/product-demo`): on desktop the
 * scripted chat + canvas, loaded as its own chunks; everywhere else, and in
 * the server render, only the demo's light poster (its frame, the chat
 * column and the app header), so a phone never downloads the demo and a
 * desktop sees no shift when it mounts. A demo that fails to load or to run
 * falls back to that poster inside the frame, never the page.
 */
export function HeroProductDemo() {
  const isDesktopViewport = useSyncExternalStore(
    subscribeToDesktop,
    isDesktop,
    isDesktopOnServer
  )
  if (!isDesktopViewport) {
    return <ProductDemoPoster />
  }
  return (
    <DeferredPanelBoundary
      fallback={<ProductDemoPoster />}
      panelName="home_hero_product_demo"
    >
      {/* The demo's catalogs load with it, as their own chunks: its own
          words and those of the app components it plays (the chat, the
          canvas, the email editor, the app header). `common` and `ui`
          come from the marketing root. The poster shows while they load. */}
      <Suspense fallback={<ProductDemoPoster />}>
        <LazyMessages
          namespaces={[
            'productDemo',
            'chat',
            'emails',
            'emailEditor',
            'appShell',
          ]}
        >
          <LazyProductDemo />
        </LazyMessages>
      </Suspense>
    </DeferredPanelBoundary>
  )
}
